import React, { useEffect, useState } from 'react';
import TagSelect from '@Shared.Angular/flowingly.components/TagSelect/TagSelect';
import TagList from '@Shared.Angular/flowingly.components/TagList/TagList';
import ITagGroupDetail from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/tagGroupDetail';
import { Guid } from '@Shared.Angular/@types/guid';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';

export type Props = {
  taggedEntityType: string;
  tags: Guid[];
  editable: boolean;
  onSaved: (tags: Guid[]) => void;
};

const TagEditor = ({ taggedEntityType, tags, editable, onSaved }: Props) => {
  const tagGroupApiService = getService('tagGroupApiService');
  const pubsubService = getService('pubsubService');
  const subscriberId = 'TagEditorComponent';

  const [editMode, setEditMode] = useState(false);
  const [tagGroups, setTagGroups] = useState<ITagGroupDetail[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<Guid[]>([]);
  const [tempSelectedTagIds, setTempSelectedTagIds] = useState<Guid[]>([]);

  useEffect(() => {
    getTagGroups();
    pubsubService.subscribeLate(
      'TAG_GROUP_DELETED',
      getTagGroups,
      subscriberId
    );
    pubsubService.subscribeLate(
      'TAG_GROUP_CREATED',
      getTagGroups,
      subscriberId
    );
    pubsubService.subscribeLate(
      'TAG_GROUP_UPDATED',
      getTagGroups,
      subscriberId
    );
    return () => {
      pubsubService.unsubscribeAll(subscriberId);
    };
  }, []);

  const getTagGroups = () => {
    tagGroupApiService.getAll().then((newTagGroups) => {
      setTagGroups(newTagGroups);
    });
  };

  useEffect(() => {
    setSelectedTagIds(tags || []);
  }, [tags]);

  const handleCancel = () => {
    setTempSelectedTagIds(selectedTagIds);
    setEditMode(false);
  };

  const handleSave = () => {
    setEditMode(false);
    setSelectedTagIds(tempSelectedTagIds);
    onSaved?.(tempSelectedTagIds);
  };

  const groupedSelectedTags = tagGroups.reduce<
    Record<string, { tags: string[]; color: string }>
  >((acc, group) => {
    const matched = group.tags.filter((tag) => selectedTagIds.includes(tag.id));
    if (matched.length > 0) {
      acc[group.name] = {
        tags: matched.map((t) => t.name),
        color: group.color
      };
    }
    return acc;
  }, {});

  return (
    <div className="tag-editor-container">
      <label className="title">{taggedEntityType} Tags</label>
      {!editMode && (
        <div>
          <TagList groupedSelectedTags={groupedSelectedTags} />
          <div className="add-remove-button-container">
            {editable && (
              <button
                type="button"
                className="btn-text-only add-remove-button"
                onClick={() => {
                  if (editable) {
                    setTempSelectedTagIds([...selectedTagIds]);
                    setEditMode(true);
                  }
                }}
              >
                Add or Remove
              </button>
            )}
          </div>
        </div>
      )}
      {editMode && (
        <div className="tag-editor-select-container">
          <TagSelect
            allTagGroups={tagGroups}
            selectedTags={tempSelectedTagIds}
            onChange={(updated: Guid[]) => setTempSelectedTagIds(updated)}
          />
          <div className="tag-actions-container">
            <button
              type="button"
              onClick={handleCancel}
              className="btn-text-only"
            >
              Cancel
            </button>
            <button type="button" onClick={handleSave} className="green btn">
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TagEditor;
